import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Shield, 
  ArrowLeft,
  Database,
  Lock,
  Eye,
  Trash2,
  Download,
  Globe,
  Clock,
  Mail,
  Server,
  Cookie,
  UserCheck,
  FileText
} from "lucide-react";

const PrivacyPage = () => {
  const currentDate = "Mars 2026";

  const dataTypes = [
    {
      category: "Données d'identification",
      icon: UserCheck,
      items: [
        "Nom complet",
        "Adresse email",
        "Photo de profil",
        "Biographie"
      ],
      purpose: "Créer et gérer votre compte, vous identifier sur la plateforme"
    },
    {
      category: "Données d'utilisation",
      icon: Eye,
      items: [
        "Évaluations données et reçues",
        "Interactions avec d'autres utilisateurs",
        "Date et heure de connexion",
        "Appareil utilisé"
      ],
      purpose: "Améliorer le service, calculer votre niveau et vos badges"
    },
    {
      category: "Contenu utilisateur",
      icon: Database,
      items: [
        "Photos et vidéos (preuves)",
        "Commentaires",
        "Signalements"
      ],
      purpose: "Permettre les fonctionnalités de l'application"
    },
    {
      category: "Données de paiement",
      icon: Lock,
      items: [
        "Historique des abonnements",
        "Références de transaction"
      ],
      purpose: "Gérer votre abonnement (paiements traités par Stripe)"
    }
  ];

  const rights = [
    {
      right: "Droit d'accès",
      description: "Vous pouvez demander une copie de toutes vos données personnelles",
      icon: Eye
    },
    {
      right: "Droit de rectification",
      description: "Vous pouvez corriger vos informations personnelles à tout moment",
      icon: FileText
    },
    {
      right: "Droit à l'effacement",
      description: "Vous pouvez demander la suppression de votre compte et de vos données",
      icon: Trash2
    },
    {
      right: "Droit à la portabilité",
      description: "Vous pouvez exporter vos données dans un format lisible",
      icon: Download
    },
    {
      right: "Droit d'opposition",
      description: "Vous pouvez vous opposer au traitement de vos données à des fins marketing",
      icon: Shield
    },
    {
      right: "Droit de limitation",
      description: "Vous pouvez demander la limitation du traitement de vos données",
      icon: Lock
    }
  ];

  return (
    <div className="min-h-screen bg-[#FFF0F5]">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-pink-100">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-extrabold text-xl" style={{ fontFamily: 'Outfit' }}>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-200 to-pink-100 flex items-center justify-center">
              <Shield size={18} className="text-[#FF3366]" fill="#FF3366" />
            </div>
            <span className="text-slate-900">SHARE</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="rounded-full">
              <ArrowLeft size={18} className="mr-2" />
              Retour
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="space-y-8">
          {/* Title */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF3366]/10 text-[#FF3366] text-sm font-medium">
              <Lock size={16} />
              Protection des données
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Politique de Confidentialité
            </h1>
            <p className="text-slate-500">Version en vigueur : {currentDate}</p>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Chez SHARE, nous prenons la protection de vos données personnelles très au sérieux. 
              Cette politique explique comment nous collectons, utilisons et protégeons vos informations.
            </p>
          </div>

          {/* RGPD Badge */}
          <Card className="guardian-card border-green-200 bg-green-50">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Shield className="text-green-600" size={32} />
              </div>
              <div>
                <h3 className="font-bold text-green-800 text-lg">Conforme au RGPD</h3>
                <p className="text-green-700">
                  RATE⭐SHARE respecte le Règlement Général sur la Protection des Données (RGPD) européen 
                  et le Digital Services Act (DSA).
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 1: Responsable */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <UserCheck className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">1. Responsable du traitement</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6">
                <p className="text-slate-600 mb-4">
                  Le responsable du traitement de vos données personnelles est :
                </p>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <p className="font-bold text-slate-900">SAS RATESHARE</p>
                  <p className="text-slate-600 text-sm mt-2">
                    Société par Actions Simplifiée<br />
                    Siège social : Lyon, France
                  </p>
                  <p className="text-slate-600 text-sm mt-3">
                    <span className="font-medium">Délégué à la Protection des Données (DPO) :</span><br />
                    Samy Cherif, Président & CEO
                  </p>
                  <p className="text-slate-600 text-sm mt-2">
                    <span className="font-medium">Contact DPO :</span>{" "}
                    <a href="mailto:contact@rateshare.fr" className="text-[#FF3366] hover:underline">contact@rateshare.fr</a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 2: Données collectées */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <Database className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">2. Données que nous collectons</h2>
            </div>
            <div className="space-y-4">
              {dataTypes.map((type, index) => (
                <Card key={index} className="guardian-card">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <type.icon className="text-slate-600" size={20} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-slate-900 mb-2">{type.category}</h3>
                        <ul className="list-disc list-inside text-slate-600 text-sm mb-3">
                          {type.items.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                        <p className="text-slate-500 text-sm">
                          <strong>Finalité :</strong> {type.purpose}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Section 3: Utilisation */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <Server className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">3. Comment nous utilisons vos données</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6">
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-3">
                    <span className="text-[#FF3366] font-bold">•</span>
                    <span><strong>Fournir le service :</strong> Créer votre compte, afficher votre profil, permettre les évaluations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#FF3366] font-bold">•</span>
                    <span><strong>Améliorer l'expérience :</strong> Analyser l'utilisation pour optimiser les fonctionnalités</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#FF3366] font-bold">•</span>
                    <span><strong>Sécurité :</strong> Détecter et prévenir les fraudes, protéger contre les abus</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#FF3366] font-bold">•</span>
                    <span><strong>Communication :</strong> Vous envoyer des notifications importantes sur votre compte</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#FF3366] font-bold">•</span>
                    <span><strong>Obligations légales :</strong> Répondre aux demandes des autorités compétentes</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Section 4: Partage */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <Globe className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">4. Partage de vos données</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6 space-y-4">
                <p className="text-slate-600">
                  Nous ne vendons <strong>jamais</strong> vos données personnelles. Nous les partageons uniquement avec :
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-4 rounded-xl">
                    <h4 className="font-bold text-slate-900 mb-2">Prestataires techniques</h4>
                    <ul className="text-slate-600 text-sm space-y-1">
                      <li>• Hébergement (serveurs sécurisés)</li>
                      <li>• Stockage des fichiers (photos/vidéos)</li>
                      <li>• Paiements (Stripe)</li>
                      <li>• Emails (Resend)</li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-xl">
                    <h4 className="font-bold text-slate-900 mb-2">Autorités légales</h4>
                    <ul className="text-slate-600 text-sm space-y-1">
                      <li>• Sur demande judiciaire</li>
                      <li>• En cas d'obligation légale</li>
                      <li>• Pour protéger nos droits</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 5: Conservation */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <Clock className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">5. Durée de conservation</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-3 px-4 font-bold text-slate-700">Type de données</th>
                        <th className="text-left py-3 px-4 font-bold text-slate-700">Durée de conservation</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-3 px-4 text-slate-600">Données de compte</td>
                        <td className="py-3 px-4 text-slate-900">Jusqu'à suppression du compte + 1 an</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4 text-slate-600">Évaluations et commentaires</td>
                        <td className="py-3 px-4 text-slate-900">Jusqu'à suppression + 3 mois</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4 text-slate-600">Photos et vidéos (preuves)</td>
                        <td className="py-3 px-4 text-slate-900">Jusqu'à suppression + 1 an</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4 text-slate-600">Données de connexion</td>
                        <td className="py-3 px-4 text-slate-900">12 mois</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4 text-slate-600">Données de paiement</td>
                        <td className="py-3 px-4 text-slate-900">10 ans (obligation légale)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 6: Vos droits */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <Shield className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">6. Vos droits (RGPD)</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {rights.map((item, index) => (
                <Card key={index} className="guardian-card">
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="text-[#FF3366]" size={18} />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">{item.right}</h4>
                      <p className="text-slate-600 text-sm">{item.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Card className="guardian-card border-[#FF3366]/20 bg-[#FF3366]/5">
              <CardContent className="p-4">
                <p className="text-slate-700">
                  <strong>Pour exercer vos droits :</strong> Envoyez un email à{" "}
                  <a href="mailto:contact@rateshare.fr" className="text-[#FF3366] font-medium">contact@rateshare.fr</a>{" "}
                  avec une copie de votre pièce d'identité. Nous répondons sous 30 jours.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Section 7: Cookies */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <Cookie className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">7. Cookies</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6 space-y-4">
                <p className="text-slate-600">
                  SHARE utilise des cookies pour :
                </p>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    <span><strong>Cookies essentiels :</strong> Permettre la connexion et le fonctionnement de base</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    <span><strong>Cookies de session :</strong> Maintenir votre connexion active</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">⚠</span>
                    <span><strong>Cookies analytiques :</strong> Comprendre comment vous utilisez l'application (avec votre consentement)</span>
                  </li>
                </ul>
                <p className="text-slate-500 text-sm">
                  Vous pouvez gérer vos préférences de cookies dans les paramètres de votre navigateur.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Section 8: Sécurité */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <Lock className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">8. Sécurité des données</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6">
                <p className="text-slate-600 mb-4">
                  Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles :
                </p>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start gap-2">
                    <Lock className="text-[#FF3366] mt-0.5 flex-shrink-0" size={16} />
                    <span>Chiffrement des données en transit (HTTPS/TLS)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Lock className="text-[#FF3366] mt-0.5 flex-shrink-0" size={16} />
                    <span>Mots de passe hashés avec bcrypt</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Lock className="text-[#FF3366] mt-0.5 flex-shrink-0" size={16} />
                    <span>Accès limité aux données personnelles</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Lock className="text-[#FF3366] mt-0.5 flex-shrink-0" size={16} />
                    <span>Sauvegardes régulières et sécurisées</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Lock className="text-[#FF3366] mt-0.5 flex-shrink-0" size={16} />
                    <span>Surveillance des accès et journalisation</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Section 9: Contact */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FF3366]/10 flex items-center justify-center">
                <Mail className="text-[#FF3366]" size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">9. Nous contacter</h2>
            </div>
            <Card className="guardian-card">
              <CardContent className="p-6 text-center">
                <p className="text-slate-600 mb-4">
                  Pour toute question concernant cette politique de confidentialité ou vos données personnelles :
                </p>
                <div className="bg-slate-50 p-4 rounded-xl inline-block">
                  <p className="font-bold text-slate-900">RATE⭐SHARE - Délégué à la Protection des Données</p>
                  <a href="mailto:contact@rateshare.fr" className="text-[#FF3366] font-medium text-lg">contact@rateshare.fr</a>
                </div>
                <p className="text-slate-500 text-sm mt-4">
                  Vous avez également le droit de déposer une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés).
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Back button */}
          <div className="text-center pt-8">
            <Link to="/">
              <Button className="btn-guardian rounded-full">
                <ArrowLeft size={18} className="mr-2" />
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-pink-100 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-slate-500 text-sm">
          <p>© 2026 RATE⭐SHARE - Le GPS de la Bienveillance</p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/terms" className="hover:text-[#FF3366]">CGU</Link>
            <Link to="/community-guidelines" className="hover:text-[#FF3366]">Règles</Link>
            <Link to="/privacy" className="hover:text-[#FF3366]">Confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
